import { QueryFunctionContext, UseQueryOptions, useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import CuratorProfileDTO from "../dto/CuratorProfileDTO";
import DoctorProfileDTO from "../dto/DoctorProfileDTO";
import { DoctorWithAccidentDTO } from "../dto/DoctorWithAccidentDTO";
import { ResearchDoctorDTO } from "../dto/ResearchDoctorDTO";
import { ResearchAgentDoctor } from "../dto/ResearchViewDTO";
import { AgentDTO, UserEditDTO, UserDTO } from "../dto/UsersDTO";
import { resolveBlob } from "../util/common";

export async function getHelp(): Promise<File> {
    const response = await axios.get(`api/Users/GetHelp`, { responseType: "blob" });
    return resolveBlob(response);
}

export async function getCuratorProfile(): Promise<CuratorProfileDTO> {
    const response = await axios.get(`api/Users/GetCuratorProfile`);
    return (response.data.result as CuratorProfileDTO);
}

export async function getDoctorProfile(): Promise<DoctorProfileDTO> {
    const response = await axios.get(`api/Users/GetDoctorProfile`);
    return (response.data.result as DoctorProfileDTO);
}

export async function getResearchDoctors(context: QueryFunctionContext): Promise<ResearchDoctorDTO[]> {
    const response = await axios.get(`/api/Users/GetDoctorsByResearch?id=${context.queryKey[2]}`);
    return (response.data.result.doctors as ResearchDoctorDTO[]);
}

export async function getDoctorsWithAccidents(context: QueryFunctionContext): Promise<DoctorWithAccidentDTO[]> {
    const response = await axios.get(`/api/Users/GetDoctorsWithAccidents?researchId=${context.queryKey[2]}`);
    return (response.data as DoctorWithAccidentDTO[]);
}

export const useGetUserEditQuery = (
    id: string | undefined,
    options?: UseQueryOptions<UserEditDTO, AxiosError>
) => {
    return useQuery<UserEditDTO, AxiosError>({
        ...options,
        queryKey: ["GetUserEdit", id],
        queryFn: async () => {
            return (await axios.get(`/api/Users/GetUserEdit`, { params: { id } })).data;
        }
    });
};

export const useGetUsersQuery = () => {
    return useQuery<UserDTO[], AxiosError>({
        queryKey: ["GetUsers"],
        queryFn: async () => {
            return (await
                axios.get(`/api/Users/GetUsers`))
                .data;
        },
        initialData: []
    });
};

export const useGetAgentsQuery = () => {
    return useQuery<AgentDTO[], AxiosError>({
        queryKey: ["GetAgents"],
        queryFn: async () => {
            return (await
                axios.get(`/api/Users/GetAgents`))
                .data;
        },
        initialData: []
    });
};

export async function fetchAgentDoctors(params: { id: string, researchId: number }) {
    const response = await axios.get(`api/Users/GetAgentDoctors`, { params });
    return response.data.result.doctors;
}

export const useGetCurrentAgentDoctors = (researchId: number) => {
    return useQuery<ResearchAgentDoctor[], AxiosError>({
        queryKey: ["Users", "GetCurrentAgentDoctors", researchId],
        queryFn: async () => {
            return (await
                axios.get(`/api/Users/GetCurrentAgentDoctors?researchId=${researchId}`))
                .data;
        },
        enabled: researchId !== null
    });
};

export async function getUsersForEditTableData(params: QueryFunctionContext) {
    const response = await axios.get(`api/Users/GetUser`, { params: { id: params.queryKey[1] } });
    return response.data;
};

export async function deleteUser(id: string) {
    return await axios.post(`api/Users/DeleteUser?id=${id}`);
};

export async function changePassword(params: { id: string, sendSms: boolean, sendMail: boolean, password: string }) {
    return await axios.post(`api/Users/ChangePassword`, { ...params });
};

//export async function getAgentsForUserEdit(): Promise<AgentDTO[]> {
//    const response = await axios.get(`/api/Users/GetUsers`);
//    return (response.data as AgentDTO[]).filter(a => a.role === AGENT_ROLE);
//}

export async function postUserForEdit(params: { type: string, sendSms: boolean, sendMail: boolean, userData: UserEditDTO }) {
    const userForm = {
        id: params.userData.id ?? null,
        name: params.userData.name,
        surname: params.userData.surname,
        middlename: !!params.userData.middlename?.length ?
            params.userData.middlename :
            "",
        role: params.userData.role,
        email: params.userData.email,
        agents: params.userData.agents,
        phoneNumber: !!params.userData.phoneNumber?.length ?
            params.userData.phoneNumber :
            "",
        password: params.userData.password,
        sendMail: params.sendMail,
        sendSms: params.sendSms,
        blocked: params.userData.blocked
    };

    const response = await axios.post(
        params.type === 'add' ?
            `/api/Users/AddUser` :
            `/api/Users/EditUser`,
        {
            ...userForm
        }
    );
    return response;
}